import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspaceEvents } from "src/store/api/events/helpers";

import { DeviceState } from "@devices/types";
import { Box, Divider, Paper, TableContainer, TableRow, Typography } from "@mui/material";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { PaneElement } from "@sidePanel/shared/PaneElement";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { useStyles } from "./NetworkTab.styles";
import Utils from "./NetworkTab.utils";

type Props = {
    deviceId: number;
    deviceState: DeviceState;
};

const NetworkTab = (props: Props) => {
    const { t } = useTranslation(["common", "sidepanels"]);
    const { classes } = useStyles();

    const { properties, networks = [], wifi, ethernet, telephony } = props.deviceState;
    const sortedNetworks = _.orderBy(networks, [(n) => n.interface.toLowerCase()], ["asc"]);

    const { data: eventReportsData, isLoading } = useCurrentWorkspaceEvents({
        entityId: props.deviceId.toString(),
        category: "device",
    });
    const reports = eventReportsData?.list || [];
    const data: API.DeviceLocationEvent[] = React.useMemo(
        () => reports.filter((event) => event.eventTypeName === "DeviceEvent.LocationChanged") as API.DeviceLocationEvent[],
        [reports],
    );

    const locationTable = useReactTable<API.DeviceLocationEvent>({
        data,
        columns: Utils.columns,
        initialState: {
            sorting: [{ id: "timestamp", desc: true }],
            pagination: {
                pageSize: 12,
                pageIndex: 0,
            },
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const { pageSize, pageIndex } = locationTable.getState().pagination;

    const adbSetting = props.deviceState?.settings?.filter((setting) => setting.key === "adb_enabled")[0];
    const ntpSetting = props.deviceState?.settings?.filter((setting) => setting.key === "ntp_server")[0];
    const deviceLocation = props.deviceState?.location;

    return (
        <PaneElement.Container>
            <PaneElement.Section>
                <PaneElement.Header>{t("common:label.general")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.adbStatus")}</PropertyKey>
                    <PropertyValue>{Utils.getHumabAdbStatus(adbSetting)}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.hostname")}</PropertyKey>
                    <PropertyValue>{properties?.["net.hostname"] ? properties["net.hostname"] : "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.ntpServer")}</PropertyKey>
                    <PropertyValue>{ntpSetting ? ntpSetting?.value : "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.dns1")}</PropertyKey>
                    <PropertyValue className={classes.ip}>
                        {properties?.["net.dns1"] ? properties["net.dns1"] : "-"}
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.dns2")}</PropertyKey>
                    <PropertyValue className={classes.ip}>
                        {properties?.["net.dns2"] ? properties["net.dns2"] : "-"}
                    </PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.networkTab.bluetooth")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("common:label.address")}</PropertyKey>
                    <PropertyValue>{props.deviceState?.bluetooth?.address ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.status")}</PropertyKey>
                    <PropertyValue>{props.deviceState?.bluetooth?.enabled ? "Enabled" : "Disabled"}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.networkTab.telephony")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.imei")}</PropertyKey>
                    <PropertyValue>{telephony?.imei ? telephony.imei : "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow style={{ alignItems: "flex-start" }}>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.sims")}</PropertyKey>
                    <Box style={{ width: "100%" }}>
                        {telephony?.sims?.length
                            ? telephony.sims.map((sim, index) => (
                                  <React.Fragment key={sim.id}>
                                      {index > 0 && <Divider className={classes.simDivider} />}
                                      <Typography variant="body1" className={classes.simSlotHeader}>
                                          {t("sidepanels:devicePanel.networkTab.simSlot")}: {sim.id}
                                      </Typography>
                                      <Box className={classes.simGrid}>
                                          <Typography variant="body1" component="span">
                                              {t("sidepanels:devicePanel.networkTab.dataEnabled")}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {sim.dataEnabled === undefined
                                                  ? "-"
                                                  : sim.dataEnabled
                                                    ? t("sidepanels:devicePanel.networkTab.enabled")
                                                    : t("sidepanels:devicePanel.networkTab.disabled")}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {t("sidepanels:devicePanel.networkTab.simNumber")}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {sim.msisdn || "-"}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {t("sidepanels:devicePanel.networkTab.carrierName")}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {sim.carrierName || "-"}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {t("common:label.displayName")}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {sim.displayName || "-"}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {t("sidepanels:devicePanel.networkTab.iccid")}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {sim.iccid || "-"}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {t("sidepanels:devicePanel.networkTab.imsi")}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {sim.imsi || "-"}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {t("sidepanels:devicePanel.networkTab.ipAddress")}
                                          </Typography>
                                          <Typography variant="body1" component="span" className={classes.ip}>
                                              {sim.ip || "-"}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {t("sidepanels:devicePanel.networkTab.signal")}
                                          </Typography>
                                          <Typography variant="body1" component="span">
                                              {Utils.getSignal(sim)}
                                          </Typography>
                                      </Box>
                                  </React.Fragment>
                              ))
                            : "-"}
                    </Box>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.networkTab.ethernetMode")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.interface")}</PropertyKey>
                    <PropertyValue>{ethernet?.interface ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.mode")}</PropertyKey>
                    <PropertyValue>{ethernet?.mode ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.ipAddress")}</PropertyKey>
                    <PropertyValue className={classes.ip}>{ethernet?.ip ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.gateway")}</PropertyKey>
                    <PropertyValue className={classes.ip}>{ethernet?.gateway ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.netmask")}</PropertyKey>
                    <PropertyValue className={classes.ip}>{ethernet?.netmask ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.dns")}</PropertyKey>
                    <PropertyValue className={classes.ip}>{ethernet?.dns ?? "-"}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.networkTab.ethernetProxy")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.mode")}</PropertyKey>
                    <PropertyValue>{ethernet?.proxyMode ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.host")}</PropertyKey>
                    <PropertyValue className={classes.ip}>{ethernet?.proxyHost ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.port")}</PropertyKey>
                    <PropertyValue>{ethernet?.proxyPort ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.pacUrl")}</PropertyKey>
                    <PropertyValue>{"-"}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.networkTab.wifi")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.status")}</PropertyKey>
                    <PropertyValue>
                        {wifi?.enabled
                            ? t("sidepanels:devicePanel.networkTab.enabled")
                            : t("sidepanels:devicePanel.networkTab.disabled")}
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.ssid")}</PropertyKey>
                    <PropertyValue>{wifi?.enabled && wifi?.ssid ? wifi?.ssid : "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.rssi")}</PropertyKey>
                    <PropertyValue>{wifi?.strength ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.mac")}</PropertyKey>
                    <PropertyValue>{wifi?.mac ?? "-"}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.overviewTab.location")}</PaneElement.Header>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.status")}</PropertyKey>
                    <PropertyValue>{deviceLocation?.enabled ? "Enabled" : "Disabled"}</PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.mode")}</PropertyKey>
                    <PropertyValue>{deviceLocation?.mode ?? "-"}</PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.provider")}</PropertyKey>
                    <PropertyValue>{deviceLocation?.provider ?? "-"}</PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.timestamp")}</PropertyKey>
                    <PropertyValue>{Utils.getLocationTimestamp(deviceLocation?.timestamp)}</PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.accuracy")}</PropertyKey>
                    <PropertyValue>{Utils.getAccuracy(deviceLocation?.accuracy)}</PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.altitude")}</PropertyKey>
                    <PropertyValue>{deviceLocation?.altitude ?? "-"}</PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.latitude")}</PropertyKey>
                    <PropertyValue>{Utils.getLatitudeWithSuffix(deviceLocation?.latitude ?? 0)}</PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.longitude")}</PropertyKey>
                    <PropertyValue>{Utils.getLongitudeWithSuffix(deviceLocation?.longitude ?? 0)}</PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.networkTab.speed")}</PropertyKey>
                    <PropertyValue>{deviceLocation?.speed ? deviceLocation.speed + " m/s" : "-"}</PropertyValue>
                </PropertyRow>

                <Box className={classes.tableWrapper}>
                    <TableContainer component={Paper}>
                        <MinimalTable>
                            <MinimalTableHeader>
                                {locationTable.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header} />
                                        ))}
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody
                                table={locationTable}
                                loading={isLoading}
                                noDataMessage={t("sidepanels:devicePanel.table.noLocationEvents")}
                            >
                                {locationTable.getRowModel().rows.map((row) => (
                                    <TableRow key={row.original.id}>
                                        {row
                                            .getVisibleCells()
                                            .map((cell) => flexRender(cell.column.columnDef.cell, cell.getContext()))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </TableContainer>
                    <CustomTablePagination
                        component="div"
                        rowsPerPageOptions={[12, 24, 48]}
                        count={locationTable.getFilteredRowModel().rows.length}
                        rowsPerPage={pageSize}
                        page={pageIndex}
                        onPageChange={(_event, page) => locationTable.setPageIndex(page)}
                        onRowsPerPageChange={(e) => {
                            const size = e.target.value ? Number(e.target.value) : 12;
                            locationTable.setPageSize(size);
                        }}
                    />
                </Box>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.networkTab.interfaces")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>
                        <Box className={classes.columnHeader}>{t("sidepanels:devicePanel.networkTab.hwInterface")}</Box>
                    </PropertyKey>
                    <Box className={classes.twoColumnRow}>
                        <Box className={classes.twoColumnLeft}>
                            <PropertyValue>
                                <Box className={classes.columnHeader}>{t("sidepanels:devicePanel.networkTab.hwAddress")}</Box>
                            </PropertyValue>
                        </Box>
                        <Box className={classes.twoColumnRight}>
                            <PropertyValue>
                                <Box className={classes.columnHeader}>{t("sidepanels:devicePanel.networkTab.ips")}</Box>
                            </PropertyValue>
                        </Box>
                    </Box>
                </PropertyRow>
                {sortedNetworks.map((network, i) => (
                    <PropertyRow key={i} style={{ alignItems: "flex-start" }}>
                        <PropertyKey>{network?.interface}</PropertyKey>
                        <Box className={classes.twoColumnRow}>
                            <Box className={classes.twoColumnLeft}>
                                <PropertyValue className={classes.ip}>{network?.hw}</PropertyValue>
                            </Box>
                            <Box className={classes.twoColumnRight}>
                                <PropertyValue>
                                    {Utils.sortIPs(network?.ips || []).map((ip) => (
                                        <div key={ip} className={classes.ip}>
                                            {ip}
                                        </div>
                                    ))}
                                </PropertyValue>
                            </Box>
                        </Box>
                    </PropertyRow>
                ))}
            </PaneElement.Section>
        </PaneElement.Container>
    );
};

export default NetworkTab;
